import { useMemo } from 'react';
import {
  createAddOnIdentityPool,
  splitAddOnsByProvenance,
  sumAddOnPriceInCents,
} from '@libs/services/order-add-ons';
import { Cart, Order } from 'src/trpc';

type CartLineItems = NonNullable<Cart['lineItems']>;
/** Booking-time snapshot of an add-on. Immutable: the cart is never rewritten after checkout. */
export type BookedAddOn = NonNullable<CartLineItems['addOns']>[number];
/** The live list an admin edits through OrderAddOnsDialog. */
export type LiveAddOn = NonNullable<Order['addOns']>[number];

/**
 * Booking-time price of a booked add-on, as the cart snapshot recorded it.
 *
 * `prepareCartForOrderCreation` writes `priceInCents` from `adjustedPriceInCents`, so the two
 * normally agree; older carts only carry the adjusted figure. Reading them in this order keeps
 * the amount we deduct for a removed item identical to the amount the cart subtotal charged for
 * it. It is not what a retained item is worth now - see `retainedFromBooking`.
 */
const bookedAddOnPriceInCents = (addOn: BookedAddOn): number =>
  addOn.priceInCents || addOn.adjustedPriceInCents || 0;

export interface OrderAddOnProvenance {
  /**
   * Booked items the order still carries, as the order records them now - so each carries the
   * price the order is charged for it rather than the price it was booked at. The add-ons
   * editor lets an admin reprice an item while it stays booked, and order billing charges that
   * live price, so this is what a priced list has to print to add up to the subtotal.
   *
   * Empty without a booking snapshot: `atBooking` then holds items whose booked price is
   * unknown and still sits inside the cart subtotal untouched, so pricing them off the live
   * figure would not reconcile with that subtotal either.
   */
  retainedFromBooking: LiveAddOn[];
  /** Booked items an admin has since removed, as the cart snapshot recorded them. */
  removedFromBooking: BookedAddOn[];
  /** Booked items as the order records them, whether or not a snapshot can confirm the booking. */
  atBooking: LiveAddOn[];
  /** Items bolted onto the order after it was placed. */
  addedAfterBooking: LiveAddOn[];
  /**
   * Cents to apply on top of the cart's booking-time subtotal and total: post-booking
   * additions less booked items that were removed. Zero when the order's add-ons still
   * match what was booked.
   */
  addOnAdjustmentInCents: number;
}

/**
 * Splits an order's add-ons and products by provenance and prices the difference between what
 * was booked and what the order carries now.
 *
 * The cart's line items are an immutable checkout snapshot, so every later edit - an add-on
 * bolted on, a product taken off - lives only on `order.addOns`. Reconciling the two is what
 * lets the page show, and charge for, the items the order actually has.
 *
 * `cart.lineItems.addOns` being absent means "we have no record of what was booked", which is
 * not the same as the empty array's "nothing was booked": coercing the two together would make
 * every legacy unstamped add-on look like a post-booking addition, and would let the missing
 * snapshot masquerade as proof that all booked items were removed. When it is absent we
 * reconcile nothing and adjust nothing.
 *
 * Both halves of the reconciliation match on identity - `_id` plus whether the item is a
 * product - and consume one copy per match, the same way the server stamps provenance. Matching
 * on a set of ids instead would let one surviving copy vouch for a booked quantity of two, and
 * would let a product stand in for the add-on that shares its id: either way a removed item
 * would stay listed and stay charged.
 *
 * `order` is optional so callers that render before their order query resolves - the order
 * detail page, which loads behind a spinner - can still call this unconditionally and keep
 * their hook order fixed. An absent order simply has no add-ons and no adjustment.
 */
export const useOrderAddOnProvenance = (
  order?: Order | null,
  cart?: Cart | null,
): OrderAddOnProvenance => {
  // Preserve undefined rather than defaulting to []: see the note above.
  const bookedAddOns = cart?.lineItems?.addOns;
  const orderAddOns = order?.addOns;

  const { atBooking, addedAfterBooking } = useMemo(
    () => splitAddOnsByProvenance(orderAddOns, bookedAddOns),
    [orderAddOns, bookedAddOns],
  );

  return useMemo(() => {
    // One live booked copy accounts for exactly one booked copy, so a booking that holds an
    // item twice against an order that kept one reports the other as removed.
    const retained = createAddOnIdentityPool(atBooking);

    // Without a booking snapshot there is nothing to reconcile against, so no booked item can
    // be proven removed and the booking-time totals stand as they are.
    const removedFromBooking: BookedAddOn[] = [];

    (bookedAddOns || []).forEach((addOn) => {
      if (!retained.take(addOn)) {
        removedFromBooking.push(addOn);
      }
    });

    // Every item in `atBooking` spent a booked copy to get there, so when a snapshot exists the
    // two lists hold the same items and `atBooking` is the retained set priced as billed. With
    // no snapshot nothing is proven booked, and nothing may be priced against one.
    const retainedFromBooking = bookedAddOns ? atBooking : [];

    const addOnAdjustmentInCents =
      sumAddOnPriceInCents(addedAfterBooking) -
      sumAddOnPriceInCents(
        removedFromBooking.map((addOn) => ({ priceInCents: bookedAddOnPriceInCents(addOn) })),
      );

    return {
      retainedFromBooking,
      removedFromBooking,
      atBooking,
      addedAfterBooking,
      addOnAdjustmentInCents,
    };
  }, [bookedAddOns, atBooking, addedAfterBooking]);
};
